import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { api } from '../../services/api'
import { useAuth } from '../../contexts/authContext'
import { useI18n } from '../../i18n/I18nContext'
import { getPendingPlan } from '../../lib/pendingPlan'
import { consumePendingDestination } from '../../lib/pendingDestination'
import { LoadingScreen } from '../../components/ui/LoadingScreen'

/**
 * OAuth callback page — rendered at /oauth-callback.
 *
 * Full-page redirect flow (no popup): the backend redirects the whole tab
 * here with ?code=<opaque-exchange-code>. This page exchanges it for an
 * access token via POST /auth/token-exchange, completes login through
 * AuthContext (so `user` is populated before we navigate anywhere protected
 * routes can see), and routes onward by intent.
 *
 * A popup+postMessage/localStorage version of this used to exist for a
 * "no page leave" desktop experience, but it was fragile in practice —
 * Google's Cross-Origin-Opener-Policy header severs `window.opener`,
 * mobile browsers and in-app webviews (Facebook/Instagram) block or
 * mishandle popups, and every workaround added real complexity for a
 * marginal UX gain. A full-page redirect is the standard, most reliable
 * OAuth2 pattern across browsers and devices — worth the brief page leave.
 */
const OAuth2Callback = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const { completeOAuthLogin } = useAuth()
    const { t } = useI18n()
    const ranRef = useRef(false)

    useEffect(() => {
        // StrictMode/dev double-invoke guard — the exchange code is single-use,
        // so a second call would only produce a spurious "expired code" error.
        if (ranRef.current) return
        ranRef.current = true

        const code = searchParams.get('code')

        if (!code) {
            navigate('/login?error=oauth_failed', { replace: true })
            return
        }

        // Strip the exchange code from the URL immediately — before any async operation —
        // so it never leaks via Referer headers or browser history.
        window.history.replaceState({}, '', '/oauth-callback')

        api.exchangeOAuthCode(code)
            .then(async ({ accessToken, expiresIn }) => {
                const loggedUser = await completeOAuthLogin(accessToken, expiresIn)

                const pendingPlan = getPendingPlan()
                if (pendingPlan) {
                    navigate('/checkout', { replace: true })
                    return
                }
                const destination = consumePendingDestination()
                if (destination) {
                    navigate(destination, { replace: true })
                    return
                }
                if (loggedUser) {
                    const seenKey = `dhtn-pricing-seen-${loggedUser.id}`
                    if (!localStorage.getItem(seenKey)) {
                        localStorage.setItem(seenKey, '1')
                        navigate('/pricing', { replace: true })
                        return
                    }
                }
                navigate('/dashboard', { replace: true })
            })
            .catch((error) => {
                Sentry.captureException(error)
                navigate('/login?error=oauth_failed', { replace: true })
            })
    }, [searchParams, navigate, completeOAuthLogin])

    return <LoadingScreen message={t('login.signingIn')} />
}

export default OAuth2Callback
